/**
 * CL-DESIGN-BOARDS-DB-2 — Portal tab access (pure; capability-driven).
 */

import { DESIGN_BOARD_PERMISSION_KEYS } from "./designBoardPermissionKeys";
import {
  DESIGN_BOARD_CLIENT_LIST_QUERY,
  DESIGN_BOARD_COMMENTS_LIST_QUERY,
  DESIGN_BOARD_STATUS,
} from "./projectDesignBoardFoundationModel";
import { canReadDesignBoardContent } from "./projectDesignBoardPermissionModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

export function isActiveCanonicalProjectAssignment(assignment = null) {
  if (!assignment || typeof assignment !== "object") return false;
  return safeStr(assignment.status || "active").toLowerCase() === "active";
}

export function resolveDesignBoardPermissions(permissions = {}) {
  const perms = permissions && typeof permissions === "object" ? permissions : {};
  return {
    canViewDesignBoards: perms[DESIGN_BOARD_PERMISSION_KEYS.view] === true,
    canManageDesignBoards: perms[DESIGN_BOARD_PERMISSION_KEYS.manage] === true,
    canContributeDesignBoardItems: perms[DESIGN_BOARD_PERMISSION_KEYS.contribute] === true,
    canCommentOnDesignBoards: perms[DESIGN_BOARD_PERMISSION_KEYS.comment] === true,
  };
}

function mergeDesignBoardPermissionSources(permissions = {}, assignment = null) {
  const resolved = permissions && typeof permissions === "object" ? permissions : {};
  const assignmentPerms =
    assignment?.permissions && typeof assignment.permissions === "object" ? assignment.permissions : {};
  return { ...resolved, ...assignmentPerms };
}

export function clientPortalDesignBoardsTabAllowed(permissions = {}, assignment = null) {
  if (!isActiveCanonicalProjectAssignment(assignment)) return false;
  const flags = resolveDesignBoardPermissions(mergeDesignBoardPermissionSources(permissions, assignment));
  return flags.canViewDesignBoards || flags.canManageDesignBoards;
}

export function resolveClientPortalDesignBoardsAccess({
  permissions = {},
  assignment = null,
} = {}) {
  const active = isActiveCanonicalProjectAssignment(assignment);
  const perms = resolveDesignBoardPermissions(mergeDesignBoardPermissionSources(permissions, assignment));
  const canView = active && perms.canViewDesignBoards;
  const canManage = active && perms.canManageDesignBoards;
  return {
    tabAllowed: canView || canManage,
    canView,
    canManage,
    canContribute: active && perms.canContributeDesignBoardItems && canView,
    canComment: active && perms.canCommentOnDesignBoards && canView,
    permissions: perms,
  };
}

export function canReadBoardInPortal({ board = {}, permissions = {} } = {}) {
  return canReadDesignBoardContent({
    board,
    permissions,
    isAdmin: false,
  }).allowed;
}

export function buildDesignBoardClientListQueryConstraints() {
  return DESIGN_BOARD_CLIENT_LIST_QUERY.filters.map((filter) => ({
    field: filter.field,
    op: filter.op,
    value: filter.value,
  }));
}

export function buildDesignBoardActiveCommentsQueryConstraints() {
  return DESIGN_BOARD_COMMENTS_LIST_QUERY.filters.map((filter) => ({
    field: filter.field,
    op: filter.op,
    value: filter.value,
  }));
}

export function shouldUseManagerBoardListQuery(access = {}) {
  return access.canManage === true;
}

export function boardListQueryMode(access = {}) {
  if (access.canManage) return "manager_all";
  if (access.canView) return "viewer_shared";
  return "none";
}

export function isSharedClientVisibleBoard(board = {}) {
  return (
    safeStr(board.status) === DESIGN_BOARD_STATUS.SHARED
    && board.clientVisible === true
  );
}
